import { debounce } from "@mui/material";
import { FullPlace as FullPlaceFragmentType } from "@sinch/core/entities/fragments.graphql";
import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";

import { useCachedFragment } from "@sinch/hooks/useCachedFragment";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { head, isNil, propEq } from "ramda";
import { useCallback, useMemo, useState } from "react";
import { Place, Places } from "./query.graphql";

import { PlacesQuery, PlacesQueryVariables } from "./query.types";
import { usePlaceOptions } from "./usePlaceOptions";

interface UsePlaceSelectProps {
  value: string | null;
  onChange?: (val: string | number | null) => void;
}

export const usePlaceSelect = ({ value, onChange }: UsePlaceSelectProps) => {
  const { data: places, dispatch, loading } = useLazyQueryHandler<PlacesQuery, PlacesQueryVariables>(Places);
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);

  // Try to search place in cache, if none found, try to fetch it from server
  const { data: place } = useCachedFragment({
    fragment: FullPlaceFragmentType,
    type: "Place",
    ids: [value ?? ""],
    query: Place,
    variables: {
      id: value ?? "",
    },
  });

  const firstPlace = head(place);
  const [currentSelectedPlace, setCurrentSelectedPlace] = useState<PlacesQuery["places"][0] | null>(firstPlace ?? null);

  const options = usePlaceOptions(places?.places, currentSelectedPlace, !loading);

  // Update current selected place if place from cache was updated
  useWatchDependency(() => {
    setCurrentSelectedPlace(firstPlace ?? null);
  }, [firstPlace]);

  const debounceDispatch = useMemo(() => debounce(dispatch, 200), [dispatch]);

  // An empty search loads the first records, so focusing the input opens a dropdown with places
  const search = useCallback(
    (searchValue: string) => debounceDispatch({ filter: { search: { LIKE: searchValue } }, limit: SEARCH_FIELD_LIMIT }),
    [debounceDispatch]
  );

  const handleChange = (val: string | number | null) => {
    const selectedPlace = places?.places.find(propEq("id", val));
    if (selectedPlace || isNil(val)) {
      onChange?.(val);
      setCurrentSelectedPlace(selectedPlace ?? null);
    }
  };

  return {
    options,
    search,
    onChange: handleChange,
    selectedPlace: currentSelectedPlace,
    dispatch,
    loading,
    isAddLocationDialogOpen: isAddDialogOpen,
    setIsAddLocationDialogOpen: setAddDialogOpen,
  };
};
